import { useEffect, useState } from 'react';
import { useVsjFlow } from './vsj-flow';
import { FactorIcon } from './vsj-mocks';
import { fetchPlaceReading, PlaceReadingDto } from './vsj-read-api';
import { READING } from './vsj-reading-timeline';
import { VsjStepHeader } from './VsjStepHeader';
import { VsjBrandMark, IconCrossroad, IconHouse, IconStore, IconPulse } from './vsj-icons';

const FACTOR_ICONS: Record<FactorIcon, (p: { size?: number }) => JSX.Element> = {
  connected:   IconCrossroad,
  residential: IconHouse,
  services:    IconStore,
  activity:    IconPulse,
};

const BEATS = [...READING.messages, READING.discovery];
const DISCOVERY_INDEX = BEATS.length - 1;

// ── Road class → factor sentiment ─────────────────────────────────────────────

const ROAD_CLASS_SENTIMENT: Record<string, 'positive' | 'neutral'> = {
  motorway:  'positive',
  trunk:     'positive',
  primary:   'positive',
  secondary: 'positive',
};

// ── Reading data → UI factors ─────────────────────────────────────────────────

interface Factor {
  icon: FactorIcon;
  label: string;
  description: string;
  sentiment: 'positive' | 'neutral' | 'negative';
}

function buildReadingFactors(reading: PlaceReadingDto): Factor[] {
  const factors: Factor[] = [];

  factors.push({
    icon:        'connected',
    label:       'Tipo de calle',
    description: reading.streetTypeLabel,
    sentiment:   (reading.roadClass && ROAD_CLASS_SENTIMENT[reading.roadClass]) || 'neutral',
  });

  if (reading.status === 'available' && reading.observationCount > 0) {
    factors.push({
      icon:        'activity',
      label:       'Referencias de mercado',
      description: `${reading.observationCount} a menos de ${reading.radiusM} m · ${reading.periodLabel}`,
      sentiment:   reading.observationCount >= 10 ? 'positive' : 'neutral',
    });
  }

  if (reading.status === 'available' && reading.observedTypes.length > 0) {
    factors.push({
      icon:        'residential',
      label:       'Tipos observados',
      description: reading.observedTypes.join(', '),
      sentiment:   'neutral',
    });
  }

  return factors;
}

function buildReadingSummary(reading: PlaceReadingDto | null): string {
  if (!reading) return 'Analizando el entorno de tu propiedad…';
  if (reading.status === 'insufficient_data') {
    return `${reading.streetTypeLabel} en un área sin referencias de mercado suficientes todavía.`;
  }
  if (reading.observationCount === 0) {
    return `${reading.streetTypeLabel}. Aún no hay referencias registradas en este entorno.`;
  }
  return `${reading.streetTypeLabel} con ${reading.observationCount} referencias registradas a menos de ${reading.radiusM} metros.`;
}

// ── Results panel ─────────────────────────────────────────────────────────────

function ResultsSheet({
  reading,
  fetchFailed,
  onNext,
}: {
  reading: PlaceReadingDto | null;
  fetchFailed: boolean;
  onNext: () => void;
}) {
  const factors = reading ? buildReadingFactors(reading) : [];

  return (
    <div className="vsj-env-sheet">
      <div className="vsj-env-sheet-scroll">
        <p className="vsj-section-label">Tu entorno</p>

        {fetchFailed ? (
          <p className="vsj-env-read-error">
            No pudimos completar la lectura del entorno en este momento.
            Podés continuar, pero algunos datos pueden no estar disponibles.
          </p>
        ) : (
          <p className="vsj-env-summary">{buildReadingSummary(reading)}</p>
        )}

        {!fetchFailed && factors.length > 0 && (
          <div className="vsj-env-factors">
            {factors.map((f, i) => {
              const Icon = FACTOR_ICONS[f.icon];
              return (
                <div
                  key={f.label}
                  className={`vsj-factor-card vsj-factor-card--${f.sentiment} vsj-reveal`}
                  style={{ animationDelay: `${120 + i * 80}ms` }}
                >
                  <span className="vsj-factor-icon"><Icon size={20} /></span>
                  <div className="vsj-factor-text">
                    <span className="vsj-factor-label">{f.label}</span>
                    <span className="vsj-factor-desc">{f.description}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!fetchFailed && reading?.status === 'available' && reading.observationCount > 0 && (
          <p className="vsj-env-evidence-note vsj-reveal" style={{ animationDelay: '480ms' }}>
            Encontramos <strong>{reading.observationCount} referencias</strong> de {reading.periodLabel} a
            menos de {reading.radiusM} metros.
          </p>
        )}


      </div>

      <div className="vsj-env-sheet-footer">
        <button className="vsj-btn-primary" onClick={onNext}>
          Contame de la propiedad
        </button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function VsjEnvironmentReading(): JSX.Element {
  const {
    goTo, envRead, setEnvRead, setEnvPhase,
    locateResult, readingResult, setReadingResult,
  } = useVsjFlow();
  const [reading, setReading]             = useState(!envRead);
  const [beat, setBeat]                   = useState(-1);
  const [readingFetchFailed, setReadingFetchFailed] = useState(false);

  useEffect(() => {
    if (envRead) {
      // Back-navigation: place already read — restore phase, don't replay.
      setEnvPhase('read');
      return () => setEnvPhase('idle');
    }

    setEnvPhase('reading');

    // Fire the read fetch concurrently with the 15s ceremony.
    // The response will be ready well before the panel rises.
    if (locateResult?.placeToken && readingResult === null) {
      fetchPlaceReading(locateResult.placeToken)
        .then((data) => setReadingResult(data))
        .catch(() => setReadingFetchFailed(true));
    }

    const timers: number[] = [];

    BEATS.forEach((b, i) => {
      timers.push(window.setTimeout(() => setBeat(i), b.at));
    });
    timers.push(window.setTimeout(() => {
      setReading(false);
      setEnvRead(true);
      setEnvPhase('read');
    }, READING.total));

    return () => {
      timers.forEach(clearTimeout);
      setEnvPhase('idle');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (reading) {
    const current     = beat >= 0 ? BEATS[beat] : null;
    const isDiscovery = beat === DISCOVERY_INDEX;

    return (
      <div className="vsj-step vsj-step--map vsj-step--reading">
        <div className="vsj-reading-overlay">
          {current && (
            <div
              key={beat}
              className={'vsj-reading-beat' + (isDiscovery ? ' vsj-reading-beat--discovery' : '')}
              style={{ animationDuration: `${current.dur}ms` }}
            >
              {isDiscovery && <VsjBrandMark size={26} className="vsj-reading-mark" />}
              <p className="vsj-reading-msg">{current.text}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="vsj-step vsj-step--map">
      <VsjStepHeader step={2} onBack={() => goTo('location')} overlay />
      <div className="vsj-map-space" />
      <ResultsSheet reading={readingResult} fetchFailed={readingFetchFailed} onNext={() => goTo('property')} />
    </div>
  );
}
